import React, { useEffect, useState } from "react";
import {
  FaBuilding,
  FaCloudUploadAlt,
  FaImage,
  FaMapMarkerAlt,
  FaSave,
  FaTimes,
} from "react-icons/fa";
import { toast } from "react-toastify";

import AdminDrawer from "./AdminDrawer";

import {
  buildInfrastructureFormData,
  emptyInfrastructureForm,
  mapInfrastructureToForm,
  resolveMediaUrl,
} from "../utils/infrastructuresManagerUtils";

export default function InfrastructureFormDrawer({
  isOpen,
  mode = "create",
  infrastructure,
  saving,
  onClose,
  onCreate,
  onUpdate,
}) {
  const isEdit = mode === "edit";

  const [formData, setFormData] = useState(emptyInfrastructureForm);
  const [photoFile1, setPhotoFile1] = useState(null);
  const [photoFile2, setPhotoFile2] = useState(null);
  const [localPreview1, setLocalPreview1] = useState("");
  const [localPreview2, setLocalPreview2] = useState("");
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (!isOpen) return;

    if (isEdit && infrastructure) {
      setFormData(mapInfrastructureToForm(infrastructure));
    } else {
      setFormData(emptyInfrastructureForm);
    }

    setPhotoFile1(null);
    setPhotoFile2(null);
    setLocalPreview1("");
    setLocalPreview2("");
    setUploading(false);
  }, [isOpen, isEdit, infrastructure]);

  useEffect(() => {
    if (!photoFile1) return undefined;

    const url = URL.createObjectURL(photoFile1);
    setLocalPreview1(url);

    return () => URL.revokeObjectURL(url);
  }, [photoFile1]);

  useEffect(() => {
    if (!photoFile2) return undefined;

    const url = URL.createObjectURL(photoFile2);
    setLocalPreview2(url);

    return () => URL.revokeObjectURL(url);
  }, [photoFile2]);

  const title = isEdit
    ? "Modifier une infrastructure"
    : "Ajouter une infrastructure";

  const subtitle = isEdit
    ? "Mettez à jour les informations, photos, adresse et coordonnées GPS."
    : "Créez un nouvel équipement municipal affiché sur le portail.";

  const preview1 =
    localPreview1 ||
    resolveMediaUrl(
      formData.photo_url1,
      "/images/asso_placeholder.webp",
    );

  const preview2 =
    localPreview2 ||
    resolveMediaUrl(
      formData.photo_url2,
      "/images/asso_placeholder.webp",
    );

  const handleChange = (event) => {
    const { name, value } = event.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  



  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!formData.nom_infrastructure) {
      toast.error("Le nom de l'infrastructure est obligatoire.");
      return;
    }

    const payload = buildInfrastructureFormData(
      formData,
      photoFile1,
      photoFile2,
    );

    if (isEdit) {
      await onUpdate?.(infrastructure.id, payload);
    } else {
      await onCreate?.(payload);
    }
  };

  return (
    <AdminDrawer
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      subtitle={subtitle}
      width="max-w-5xl"
    >
      <form onSubmit={handleSubmit} className="flex min-h-full flex-col">
        <div className="flex-1 space-y-5 p-4 sm:p-6">
          <FormBlock
            icon={<FaBuilding />}
            title="Informations principales"
            subtitle="Nom, description et ordre d’affichage de l’équipement."
          >
            <div className="grid gap-4 sm:grid-cols-2">
              <InputField
                label="Nom de l'infrastructure"
                name="nom_infrastructure"
                value={formData.nom_infrastructure}
                onChange={handleChange}
                required
              />

              <InputField
                label="Ordre d'affichage"
                name="ordre_infrastructure"
                type="number"
                value={formData.ordre_infrastructure}
                onChange={handleChange}
              />

              <div className="sm:col-span-2">
                <label className="mb-1 block text-sm font-bold text-slate-700 dark:text-slate-200">
                  Description
                </label>

                <textarea
                  name="description_infrastructure"
                  value={formData.description_infrastructure}
                  onChange={handleChange}
                  rows={5}
                  className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:ring-2 focus:ring-primary/40 dark:border-white/10 dark:bg-slate-950 dark:text-white"
                />
              </div>
            </div>
          </FormBlock>

          <FormBlock
            icon={<FaMapMarkerAlt />}
            title="Localisation"
            subtitle="Adresse complète et coordonnées GPS au format latitude, longitude."
          >
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <InputField
                  label="Adresse"
                  name="adresse_infrastructure"
                  value={formData.adresse_infrastructure}
                  onChange={handleChange}
                />
              </div>

              <InputField
                label="Coordonnées GPS"
                name="gps_infrastructure"
                value={formData.gps_infrastructure}
                onChange={handleChange}
                placeholder="44.8659, -0.5972"
              />

              <div className="rounded-2xl border border-blue-200 bg-blue-50 p-4 text-sm leading-6 text-blue-700 dark:border-blue-500/20 dark:bg-blue-500/10 dark:text-blue-300">
                Exemple attendu : <strong>44.8659, -0.5972</strong>
                <br />
                Ces coordonnées permettent d’afficher correctement le marqueur
                sur la carte publique.
              </div>
            </div>
          </FormBlock>

          <FormBlock
            icon={<FaImage />}
            title="Photos"
            subtitle="Ajoutez une URL ou téléversez une image pour chaque photo."
          >
            <div className="grid gap-5 lg:grid-cols-2">
              <PhotoField
                label="Photo 1"
                urlName="photo_url1"
                urlValue={formData.photo_url1}
                onUrlChange={handleChange}
                file={photoFile1}
                onFileChange={setPhotoFile1}
                preview={preview1}
              />

              <PhotoField
                label="Photo 2"
                urlName="photo_url2"
                urlValue={formData.photo_url2}
                onUrlChange={handleChange}
                file={photoFile2}
                onFileChange={setPhotoFile2}
                preview={preview2}
              />
            </div>
          </FormBlock>
        </div>

        <div className="flex justify-end gap-2 border-t border-slate-200 p-4 dark:border-white/10 sm:px-6">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-700 transition hover:bg-slate-50 disabled:opacity-60 dark:border-white/10 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-white/10"
          >
            <FaTimes />
            Annuler
          </button>

          <button
            type="submit"
            disabled={saving}
            className="inline-flex items-center gap-2 rounded-2xl bg-primary px-4 py-2 text-sm font-bold text-white transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <FaSave />
            {saving ? "Enregistrement..." : isEdit ? "Mettre à jour" : "Créer"}
          </button>
        </div>
      </form>
    </AdminDrawer>
  );
}

function FormBlock({ icon, title, subtitle, children }) {
  return (
    <div className="rounded-[24px] border border-slate-200 bg-slate-50 p-4 dark:border-white/10 dark:bg-slate-900/60">
      <div className="mb-5 flex items-center gap-3">
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-primary">
          {icon}
        </span>

        <div>
          <h3 className="font-bold text-slate-950 dark:text-white">{title}</h3>

          {subtitle && (
            <p className="text-sm text-slate-600 dark:text-slate-300">
              {subtitle}
            </p>
          )}
        </div>
      </div>

      {children}
    </div>
  );
}

function InputField({
  label,
  name,
  value,
  onChange,
  type = "text",
  required = false,
  placeholder = "",
}) {
  return (
    <div>
      <label className="mb-1 block text-sm font-bold text-slate-700 dark:text-slate-200">
        {label}
      </label>

      <input
        type={type}
        name={name}
        value={value ?? ""}
        onChange={onChange}
        required={required}
        placeholder={placeholder}
        className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:ring-2 focus:ring-primary/40 dark:border-white/10 dark:bg-slate-950 dark:text-white"
      />
    </div>
  );
}

function PhotoField({
  label,
  urlName,
  urlValue,
  onUrlChange,
  file,
  onFileChange,
  preview,
}) {
  return (
    <div className="rounded-[24px] border border-slate-200 bg-white p-4 dark:border-white/10 dark:bg-slate-950">
      <p className="mb-3 text-sm font-bold text-slate-950 dark:text-white">
        {label}
      </p>

      <div className="aspect-video overflow-hidden rounded-[20px] bg-slate-100 dark:bg-slate-900">
        <img
          src={preview}
          alt={label}
          className="h-full w-full object-cover"
          onError={(event) => {
            event.currentTarget.src = "/images/infrastructure_placeholder.jpg";
          }}
        />
      </div>

      <div className="mt-4 space-y-3">
        <div>
          <label className="mb-1 block text-sm font-bold text-slate-700 dark:text-slate-200">
            URL
          </label>

          <input
            type="text"
            name={urlName}
            value={urlValue ?? ""}
            onChange={onUrlChange}
            placeholder="https://... ou /uploads/..."
            className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:ring-2 focus:ring-primary/40 dark:border-white/10 dark:bg-slate-900 dark:text-white"
          />
        </div>

        <label className="flex cursor-pointer items-center justify-center gap-2 rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-4 py-4 text-sm font-bold text-slate-600 transition hover:border-primary hover:text-primary dark:border-white/10 dark:bg-slate-900 dark:text-slate-300">
          <FaCloudUploadAlt />
          {file ? file.name : "Choisir un fichier"}
          <input
            type="file"
            accept="image/*"
            onChange={(event) => onFileChange(event.target.files?.[0] || null)}
            className="hidden"
          />
        </label>
      </div>
    </div>
  );
}
